import React, { useState } from 'react';
import { getApiUrl } from '../../config/api'

// YouTube 비디오 ID 추출 함수
const extractVideoId = (url) => {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? match[2] : null;
};

// YouTube oEmbed API를 사용하여 비디오 정보 가져오기
const getYouTubeEmbed = async (videoId) => {
  try {
    const response = await fetch(`https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`);
    if (response.ok) {
      return await response.json();
    }
  } catch (error) {
    console.error('YouTube oEmbed API 호출 실패:', error);
  }
  return null;
};

const YoutubePlaylistsApi = () => {
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [videoPreviews, setVideoPreviews] = useState({});
  const [multiselectOpen, setMultiselectOpen] = useState({});
  const [copyStatus, setCopyStatus] = useState({});
  const [constraintAlerts, setConstraintAlerts] = useState({});

  const parameterConfigs = [
    {
      name: 'part',
      label: 'Part',
      description: '반환할 리소스 부분을 지정합니다. 여러 부분을 쉼표로 구분하여 요청할 수 있습니다.',
      type: 'select',
      required: true,
      options: [
        { value: 'snippet', label: 'snippet' },
        { value: 'contentDetails', label: 'contentDetails' },
        { value: 'status', label: 'status' },
        { value: 'snippet,contentDetails', label: 'snippet,contentDetails' },
        { value: 'snippet,status', label: 'snippet,status' },
        { value: 'contentDetails,status', label: 'contentDetails,status' },
        { value: 'snippet,contentDetails,status', label: 'snippet,contentDetails,status' }
      ]
    },
    {
      name: 'id',
      label: 'Playlist ID (쉼표로 구분)',
      description: '특정 재생목록 ID를 쉼표로 구분하여 여러 개 지정할 수 있습니다. 다른 필터와 함께 사용할 수 없습니다.',
      type: 'text',
      required: false,
      placeholder: '예: PLbpi6ZahtOH6BlwajRGYNLX75p3t35HeM'
    },
    {
      name: 'channelId',
      label: 'Channel ID',
      description: '특정 채널의 재생목록만 조회합니다. 채널 ID를 입력하세요.',
      type: 'text',
      required: false,
      placeholder: '특정 채널의 재생목록 조회'
    },
    {
      name: 'mine',
      label: 'Mine',
      description: '인증된 사용자의 재생목록만 조회합니다. OAuth 인증이 필요합니다.',
      type: 'select',
      required: false,
      options: [
        { value: 'true', label: 'true (내 재생목록)' },
        { value: 'false', label: 'false (모든 재생목록)' }
      ]
    },
    {
      name: 'maxResults',
      label: 'Max Results',
      description: '반환할 최대 결과 수를 지정합니다. 기본값은 5, 최대값은 50입니다.',
      type: 'select',
      required: false,
      options: [
        { value: '5', label: '5개' },
        { value: '10', label: '10개' },
        { value: '15', label: '15개' },
        { value: '20', label: '20개' },
        { value: '25', label: '25개' },
        { value: '30', label: '30개' },
        { value: '40', label: '40개' },
        { value: '50', label: '50개' }
      ]
    }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // 다중 선택 체크박스 핸들러
  const handleMultiselectChange = (name, value) => {
    setFormData(prev => {
      const currentValues = prev[name] ? prev[name].split(',').filter(v => v.trim()) : [];
      
      if (currentValues.includes(value)) {
        // 값이 이미 있으면 제거
        const newValues = currentValues.filter(v => v !== value);
        return {
          ...prev,
          [name]: newValues.join(',')
        };
      } else {
        // 값이 없으면 추가
        const newValues = [...currentValues, value];
        return {
          ...prev,
          [name]: newValues.join(',')
        };
      }
    });
  };

  // 전체선택/전체해제 핸들러
  const handleSelectAll = (name, options) => {
    setFormData(prev => {
      const currentValues = prev[name] ? prev[name].split(',').filter(v => v.trim()) : [];
      const allValues = options.map(option => option.value);
      
      // 현재 모든 값이 선택되어 있으면 전체 해제, 아니면 전체 선택
      const isAllSelected = allValues.every(value => currentValues.includes(value));
      
      if (isAllSelected) {
        return {
          ...prev,
          [name]: ''
        };
      } else {
        return {
          ...prev,
          [name]: allValues.join(',')
        };
      }
    });
  };

  // 다중 선택 드롭다운 토글
  const toggleMultiselect = (name) => {
    setMultiselectOpen(prev => ({
      ...prev,
      [name]: !prev[name]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const params = new URLSearchParams();
      params.append('endpoint', 'playlists');
      
      // 빈 값이 아닌 파라미터만 추가
      Object.entries(formData).forEach(([key, value]) => {
        if (value && value.trim() !== '') {
          params.append(key, value.trim());
        }
      });

      const response = await fetch(`${getApiUrl('/api/youtube/dynamic')}?${params.toString()}`);
      const data = await response.json();

      if (data.success) {
        setResult(data);
        
        // YouTube 비디오 미리보기 가져오기
        if (data.result && data.result.items && data.result.items.length > 0) {
          const previews = {};
          for (const item of data.result.items) {
            if (item.id && item.id.videoId) {
              const embedData = await getYouTubeEmbed(item.id.videoId);
              if (embedData) {
                previews[item.id.videoId] = embedData;
              }
            } else if (item.id && typeof item.id === 'string') {
              const embedData = await getYouTubeEmbed(item.id);
              if (embedData) {
                previews[item.id] = embedData;
              }
            }
          }
          setVideoPreviews(previews);
        }
      } else {
        setError(data.message || 'API 호출 실패');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // 조회 버튼 핸들러 (API 호출만)
  const handleSearch = async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const params = new URLSearchParams();
      params.append('endpoint', 'playlists');
      
      // 빈 값이 아닌 파라미터만 추가
      Object.entries(formData).forEach(([key, value]) => {
        if (value && value.trim() !== '') {
          params.append(key, value.trim());
        }
      });

      const response = await fetch(`${getApiUrl('/api/youtube/dynamic')}?${params.toString()}`);
      const data = await response.json();

      if (data.success) {
        setResult(data);
        
        // YouTube 비디오 미리보기 가져오기
        if (data.result && data.result.items && data.result.items.length > 0) {
          const previews = {};
          for (const item of data.result.items) {
            if (item.id && item.id.videoId) {
              const embedData = await getYouTubeEmbed(item.id.videoId);
              if (embedData) {
                previews[item.id.videoId] = embedData;
              }
            } else if (item.id && typeof item.id === 'string') {
              const embedData = await getYouTubeEmbed(item.id);
              if (embedData) {
                previews[item.id] = embedData;
              }
            }
          }
          setVideoPreviews(previews);
        }
      } else {
        setError(data.message || 'API 호출 실패');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // 저장 버튼 핸들러 (post 테이블에 저장)
  const handleSave = async () => {
    if (!result || !result.result || !result.result.items) {
      setError('저장할 데이터가 없습니다. 먼저 조회를 실행해주세요.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const saveParams = new URLSearchParams();
      saveParams.append('endpoint', 'playlists');
      
      // 원본 파라미터들 추가
      Object.entries(formData).forEach(([key, value]) => {
        if (value && value.trim() !== '') {
          saveParams.append(key, value.trim());
        }
      });

      // 저장 요청
      const response = await fetch(`/api/youtube/save-videos?${saveParams.toString()}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          videos: result.result.items,
          params: result.params
        })
      });

      const data = await response.json();

      if (data.success) {
        alert(`성공적으로 ${data.savedCount}개의 동영상을 저장했습니다.`);
      } else {
        setError(data.message || '저장 실패');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // 텍스트 복사 함수
  const handleCopy = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyStatus(prev => ({ ...prev, [type]: '복사됨' }));
      
      // 2초 후 상태 초기화
      setTimeout(() => {
        setCopyStatus(prev => ({ ...prev, [type]: '' }));
      }, 2000);
    } catch (err) {
      console.error('복사 실패:', err);
      setCopyStatus(prev => ({ ...prev, [type]: '복사 실패' }));
      
      setTimeout(() => {
        setCopyStatus(prev => ({ ...prev, [type]: '' }));
      }, 2000);
    }
  };

  // 제약사항 체크 함수
  const checkConstraints = (name, value) => {
    const newFormData = { ...formData, [name]: value };
    
    // playlists 엔드포인트 필터 검사
    if ('playlists' === 'playlists') {
      const hasRequiredFilter = newFormData.id || newFormData.channelId || newFormData.mine;
      if (!hasRequiredFilter && (newFormData.maxResults)) {
        const message = `⚠️ 제약사항 알림\n\nPlaylists API는 다음 중 하나의 필터가 필요합니다:\n- id (재생목록 ID)\n- channelId (채널 ID)\n- mine (내 재생목록)\n\n해결 방법:\n1. 위의 필터 중 하나를 추가하세요\n2. 특정 재생목록을 조회하려면 id를 입력하세요`;
        alert(message);
        setConstraintAlerts(prev => ({ ...prev, [name]: true }));
        return false;
      }
    }

    setConstraintAlerts(prev => ({ ...prev, [name]: false }));
    return true;
  };

  const renderParameterField = (config) => {
    const { name, label, type, options, placeholder, required, description } = config;
    
    if (type === 'checkbox' && options) {
      return (
        <div key={name} className="form-group full-width">
          <label htmlFor={name} className={required ? 'required' : ''}>{label}:</label>
          {description && <div className="parameter-description">{description}</div>}
          <div className="checkbox-group">
            {options.map(option => (
              <label key={option.value} className="checkbox-option">
                <input
                  type="checkbox"
                  value={option.value}
                  checked={formData[name] ? formData[name].split(',').includes(option.value) : false}
                  onChange={(e) => {
                    const currentValues = formData[name] ? formData[name].split(',').filter(v => v.trim()) : [];
                    let newValue;
                    
                    if (e.target.checked) {
                      newValue = [...currentValues, option.value].join(',');
                    } else {
                      newValue = currentValues.filter(v => v !== option.value).join(',');
                    }
                    
                    // 제약사항 체크
                    if (checkConstraints(name, newValue)) {
                      setFormData(prev => ({
                        ...prev,
                        [name]: newValue
                      }));
                    }
                  }}
                />
                <span className="checkbox-label">{option.label}</span>
              </label>
            ))}
          </div>
        </div>
      );
    }
    
    if (type === 'multiselect' && options) {
      const currentValues = formData[name] ? formData[name].split(',').filter(v => v.trim()) : [];
      const isOpen = multiselectOpen[name] || false;
      
      return (
        <div key={name} className="form-group">
          <label htmlFor={name} className={required ? 'required' : ''}>{label}:</label>
          {description && <div className="parameter-description">{description}</div>}
          <div className="multiselect-container">
            <div 
              className="multiselect-trigger"
              onClick={() => toggleMultiselect(name)}
            >
              <span className="multiselect-display">
                {currentValues.length > 0 
                  ? `${currentValues.length}개 선택됨` 
                  : '선택하세요'
                }
              </span>
              <span className="multiselect-arrow">▼</span>
            </div>
            {isOpen && (
              <div className="multiselect-dropdown">
                {/* 전체선택 체크박스 */}
                <label className="multiselect-option select-all-option">
                  <input
                    type="checkbox"
                    checked={currentValues.length === options.length && options.length > 0}
                    onChange={() => handleSelectAll(name, options)}
                  />
                  <span className="multiselect-option-text">전체선택</span>
                </label>
                <div className="multiselect-divider"></div>
                {options.map(option => (
                  <label key={option.value} className="multiselect-option">
                    <input
                      type="checkbox"
                      checked={currentValues.includes(option.value)}
                      onChange={() => handleMultiselectChange(name, option.value)}
                    />
                    <span className="multiselect-option-text">{option.label}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
      );
    }
    
    if (type === 'select' && options) {
      return (
        <div key={name} className="form-group">
          <label htmlFor={name} className={required ? 'required' : ''}>{label}:</label>
          {description && <div className="parameter-description">{description}</div>}
          <select
            id={name}
            name={name}
            value={formData[name] || ''}
            onChange={(e) => {
              const value = e.target.value;
              if (checkConstraints(name, value)) {
                handleInputChange(e);
              }
            }}
            required={required}
          >
            <option value="">선택하세요</option>
            {options.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      );
    }
    
    return (
      <div key={name} className="form-group">
        <label htmlFor={name} className={required ? 'required' : ''}>{label}:</label>
        {description && <div className="parameter-description">{description}</div>}
        <input
          type="text"
          id={name}
          name={name}
          value={formData[name] || ''}
          onChange={(e) => {
            const value = e.target.value;
            if (checkConstraints(name, value)) {
              handleInputChange(e);
            }
          }}
          placeholder={placeholder}
          required={required}
        />
      </div>
    );
  };

  return (
    <div className="youtube-api-tester">
      <div className="page-header">
        <h1>YouTube Playlists API 테스트</h1>
        <p>YouTube 재생목록 정보를 조회하는 API입니다. 특정 재생목록, 채널의 재생목록, 내 재생목록 등을 가져올 수 있습니다.</p>
      </div>

      <div className="form-section">
        <h3>API 파라미터 설정</h3>
        <form onSubmit={handleSubmit}>
          <div className="parameters-grid">
            {parameterConfigs.map(renderParameterField)}
          </div>
          
          <div className="form-actions">
            <button 
              type="button" 
              className="search-button"
              onClick={handleSearch}
              disabled={loading}
            >
              {loading ? '실행 중...' : '실행'}
            </button>
            <button 
              type="button" 
              className="submit-button"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? 'API 호출 중...' : 'API 호출'}
            </button>
          </div>
        </form>
      </div>

      {error && (
        <div className="error-section">
          <h3>❌ 오류</h3>
          <div className="error-message">{error}</div>
        </div>
      )}

      {result && (
        <div className="result-section">
          <div className="result-header">
            <h3>✅ API 호출 결과</h3>
            <button 
              type="button" 
              className="save-button"
              onClick={handleSave}
              disabled={loading}
            >
              {loading ? '저장 중...' : '📥 Post 테이블에 저장'}
            </button>
          </div>
          
          <div className="result-info">
            <div className="info-item">
              <strong>엔드포인트:</strong> {result.endpoint}
            </div>
            <div className="info-item">
              <strong>총 결과 수:</strong> {result.result.totalResults}
            </div>
            <div className="info-item">
              <strong>페이지당 결과 수:</strong> {result.result.resultsPerPage}
            </div>
            <div className="info-item">
              <strong>실제 반환된 항목:</strong> {result.result.items.length}개
            </div>
          </div>

          {/* YouTube 미리보기 섹션 */}
          {result.result.items && result.result.items.length > 0 && (
            <div className="youtube-previews">
              <h4>🎬 YouTube 미리보기 ({result.result.items.length}개)</h4>
              <div className="video-grid">
                {result.result.items.map((item, index) => {
                  const videoId = item.id?.videoId || (typeof item.id === 'string' ? item.id : null);
                  const preview = videoId ? videoPreviews[videoId] : null;
                  
                  if (videoId && preview) {
                    // YouTube API에서 가져온 원본 데이터에서 추가 정보 추출
                    const snippet = item.snippet || {};
                    const statistics = item.statistics || {};
                    
                    // 조회수 포맷팅
                    const formatViewCount = (count) => {
                      if (!count) return '0';
                      const num = parseInt(count);
                      if (num >= 1000000) {
                        return (num / 1000000).toFixed(1) + 'M';
                      } else if (num >= 1000) {
                        return (num / 1000).toFixed(1) + 'K';
                      }
                      return num.toString();
                    };

                    // 날짜 포맷팅
                    const formatDate = (dateString) => {
                      if (!dateString) return '';
                      const date = new Date(dateString);
                      return date.toLocaleDateString('ko-KR', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      });
                    };

                    return (
                      <div key={index} className="video-preview-item">
                        <div className="video-thumbnail">
                          <img 
                            src={snippet.thumbnails?.high?.url || preview.thumbnail_url} 
                            alt={preview.title}
                          />
                        </div>
                        <div className="video-info">
                          <h5>{preview.title}</h5>
                          <p className="video-author">{preview.author_name}</p>
                          <div className="video-stats">
                            {snippet.publishedAt && (
                              <div className="video-stat">
                                <span>📅</span>
                                <span>{formatDate(snippet.publishedAt)}</span>
                              </div>
                            )}
                            {statistics.viewCount && (
                              <div className="video-stat">
                                <span>👁️</span>
                                <span>{formatViewCount(statistics.viewCount)} 조회</span>
                              </div>
                            )}
                            {statistics.likeCount && (
                              <div className="video-stat">
                                <span>👍</span>
                                <span>{formatViewCount(statistics.likeCount)} 좋아요</span>
                              </div>
                            )}
                            {statistics.commentCount && (
                              <div className="video-stat">
                                <span>💬</span>
                                <span>{formatViewCount(statistics.commentCount)} 댓글</span>
                              </div>
                            )}
                            {snippet.duration && (
                              <div className="video-stat">
                                <span>⏱️</span>
                                <span>{snippet.duration}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  }
                  return null;
                })}
              </div>
            </div>
          )}

          <div className="parameters-display">
            <div className="display-header">
              <h4>사용된 파라미터:</h4>
              <button 
                type="button" 
                className="copy-button"
                onClick={() => handleCopy(JSON.stringify(result.params, null, 2), 'params')}
                title="파라미터 복사"
              >
                📋 {copyStatus.params || '복사'}
              </button>
            </div>
            <pre>{JSON.stringify(result.params, null, 2)}</pre>
          </div>

          <div className="results-display">
            <div className="display-header">
              <h4>결과 데이터:</h4>
              <button 
                type="button" 
                className="copy-button"
                onClick={() => handleCopy(JSON.stringify(result.result, null, 2), 'results')}
                title="결과 데이터 복사"
              >
                📋 {copyStatus.results || '복사'}
              </button>
            </div>
            <pre>{JSON.stringify(result.result, null, 2)}</pre>
          </div>
        </div>
      )}

      {/* 제약사항 안내 섹션 */}
      <div className="constraints-section">
        <h3>📋 YouTube API 제약사항</h3>
        <div className="constraints-content">
          <div className="constraint-group">
            <h4>📋 Playlists API 제약사항</h4>
            <ul>
              <li><strong>필터 필수:</strong> id, channelId, mine 중 하나는 반드시 필요합니다.</li>
              <li><strong>재생목록 접근:</strong> 공개된 재생목록만 조회 가능합니다.</li>
              <li><strong>최대 결과 수:</strong> maxResults는 1-50 사이의 값이어야 합니다.</li>
              <li><strong>OAuth 인증:</strong> mine 파라미터 사용 시 OAuth 인증이 필요합니다.</li>
            </ul>
          </div>
          
          <div className="constraint-group">
            <h4>📋 일반 제약사항</h4>
            <ul>
              <li><strong>API 할당량:</strong> 일일 10,000 유닛 제한이 있습니다.</li>
              <li><strong>재생목록 ID:</strong> YouTube 재생목록 URL에서 확인할 수 있습니다.</li>
              <li><strong>채널 ID:</strong> 채널의 재생목록을 조회할 때 사용합니다.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default YoutubePlaylistsApi;
